import {
  IPdfAnnotations,
  IPdfHighlight,
  IPdfHighlightInput,
  PopoverConfiguration,
} from "../../../typings/annotate/annotation";
import { InternalPDFHighlight } from "./types";
import { extractXfdfData, highlightsToInternalHighlights } from "./util";
import useZoomPlugin from "./zoom-plugin";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import {
  PdfJs,
  SpecialZoomLevel,
  Viewer,
  ViewMode,
} from "@react-pdf-viewer/core";
import {
  highlightPlugin,
  RenderHighlightContentProps,
  RenderHighlightsProps,
  RenderHighlightTargetProps,
} from "@react-pdf-viewer/highlight";
import * as React from "react";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useCommentContext from "../hooks/comment-context";

export function PdfViewer(props: {
  fileUrl: string;
  id: string;
  annotations: IPdfAnnotations;
  pdfRenderIncrement: () => void;
  zoomLevel: number;
  setFakeHighlight: (h: IPdfHighlight) => void;
  // onPageChange?: (pageNumber: Number) => Promise<any>;
  // styleFile: string;
  popoverConfiguration: PopoverConfiguration;
  // openAtPage?: number;
  // endOfActions?: JSX.Element;
}) {
  const [pdfHighlights, setPdfHighlights] = React.useState<
    InternalPDFHighlight[]
  >([]);
  const [scale, setScale] = React.useState(1);
  const [doc, setDoc] = React.useState<PdfJs.PdfDocument>();
  const customZoomPluginInstance = useZoomPlugin();
  // TODO: Implement buittons to handle this
  const { zoomTo } = customZoomPluginInstance;
  const commentContext = useCommentContext();
  // let noteId = pdfHighlights.length;
  // const height = 840.96;
  // const width = 594.96;
  // const height = 425.82;
  // const width = 601.58;

  useEffect(() => {
    zoomTo(props.zoomLevel);
    setScale(props.zoomLevel);
  }, [props.zoomLevel]);

  useEffect(() => {
    if (!doc) {
      return;
    }
    if (props.popoverConfiguration.focusMode) {
      setPdfHighlights([]);
      props.pdfRenderIncrement();
      return;
    }
    highlightsToInternalHighlights(props.annotations.highlights, doc).then(
      (res) => {
        setPdfHighlights(res);
        props.pdfRenderIncrement();
      },
    );
  }, [doc, props.annotations.highlights, props.popoverConfiguration.focusMode]);

  // 180.553,582.150,287.679,601.187
  //height   left  // pageIndex  // top width
  const renderHighlightTarget = (renderProps: RenderHighlightTargetProps) => {
    if (!doc) {
      return <></>;
    }
    return (
      <div
        style={{
          background: "#eee",
          display: "flex",
          position: "absolute",
          left: `${renderProps.selectionRegion.left}%`,
          top: `${
            renderProps.selectionRegion.top + renderProps.selectionRegion.height
          }%`,
          transform: "translate(0, 8px)",
          zIndex: 21,
        }}
      >
        <HighlightTarget
          renderProps={renderProps}
          popoverConfiguration={props.popoverConfiguration}
          doc={doc}
          setFakeHighlight={(h) => props.setFakeHighlight(h)}
        />
      </div>
    );
  };

  const renderHighlightContent = (props: RenderHighlightContentProps) => {
    // We don't use this, we have our own hig
    return <div></div>;
  };

  const renderHighlights = (props: RenderHighlightsProps) => (
    <div className="rpv-core__annotation-layer mix-blend-multiply">
      {pdfHighlights.map((highlight) => (
        <React.Fragment key={highlight.id}>
          {highlight.highlightAreas
            .filter((area: any) => area.page === props.pageIndex)
            .map((area: any, idx: number, arr) => {
              let backgroundColor =
                highlight.color +
                (commentContext.selectedThread === highlight.id ? "95" : "70");
              if (highlight.id === "temp") {
                // For a temp highlight, we'll do some stripes.
                backgroundColor = `repeating-linear-gradient(
                  -45deg,
                  ${highlight.color}a0,
                  ${highlight.color}a0 20px,
                  ${highlight.color}50 20px,
                  ${highlight.color}50 40px
                )`;
              }

              return (
                <div
                  key={idx}
                  onClick={() => {
                    commentContext.selectThread(highlight.id as string);
                  }}
                  className={
                    "highlight mix-blend-multiply cursor-pointer thread_" +
                    highlight.id
                  }
                  style={Object.assign(
                    { background: backgroundColor },
                    props.getCssProperties(area, props.rotation),
                  )}
                >
                  {!!highlight.highlight.comments?.length &&
                    idx === arr.length - 1 && (
                      <span
                        className={
                          "inline-flex float-right pr-1 -translate-y-[150%]"
                        }
                      >
                        <ChatBubbleLeftRightIcon className="h-3 w-3 text-muted-foreground" />
                      </span>
                    )}
                </div>
              );
            })}
        </React.Fragment>
      ))}
    </div>
  );

  const highlightPluginInstance = highlightPlugin({
    renderHighlightTarget,
    renderHighlightContent,
    renderHighlights,
  });

  const style = { "--scale-factor": scale } as React.CSSProperties;
  return (
    <div className="w-full min-h-full relative" style={style}>
      <Viewer
        // This can be removed at some point when PDF-JS is updated by react-pdf-viewer.
        // This blocks the exploit of CVE-..
        // https://github.com/react-pdf-viewer/react-pdf-viewer/issues/1659
        transformGetDocumentParams={(options: PdfJs.GetDocumentParams) =>
          Object.assign({}, options, {
            isEvalSupported: false,
          })
        }
        fileUrl={props.fileUrl}
        defaultScale={SpecialZoomLevel.PageWidth}
        onDocumentLoad={(e) => {
          setDoc(e.doc);
        }}
        plugins={[highlightPluginInstance, customZoomPluginInstance]}
      />
    </div>
  );
}

// This highlight target is a little bit of hack for react-pdf-viewer. The highlights plugin
// wants absolutely to manage the popover, but I don't really want to (as they don't support popover for
// existing highlight, only for new selection).
// So using this to create the highlights data we need as a `temp` highlight, like for HTML, and then
// canceling the selection.
function HighlightTarget({
  renderProps,
  doc,
  popoverConfiguration,
  setFakeHighlight,
}: {
  renderProps: RenderHighlightTargetProps;
  doc: PdfJs.PdfDocument;
  popoverConfiguration: PopoverConfiguration;
  setFakeHighlight: (h: IPdfHighlight) => void;
}) {
  useEffect(() => {
    doc
      ?.getPage(renderProps.highlightAreas[0].pageIndex + 1)
      .then(async (page) => {
        const textContent = await page.getTextContent();
        return { page, textContent };
      })
      .then(({ page, textContent }) => {
        const pageText = textContent.items.map((x) => x.str).join(" ");

        const index = pageText.indexOf(renderProps.selectedText);
        let prefix = "";
        let suffix = "";
        if (index > 0) {
          try {
            // We can get suffix/prefix, we found the text.
            const startPrefix = Math.max(0, index - 200);
            const endSuffix = Math.min(
              pageText.length,
              index + renderProps.selectedText.length + 200,
            );
            prefix = pageText.substring(startPrefix, index);
            suffix = pageText.substring(
              index + renderProps.selectedText.length,
              endSuffix,
            );
          } catch (err) {
            console.log("Error getting prefix suffix, but don't fail");
          }
        }
        let viewport = page.getViewport({ scale: 1 });
        const xfdfData = extractXfdfData(viewport, renderProps);
        const pdfHighlight = {
          id: "temp", // TODO: fill with real data
          text: renderProps.selectedText,
          color: "#ffffff",
          model: "marker0",
          prefix: prefix,
          suffix: suffix,
          canEdit: true,
          profile: {
            ...popoverConfiguration.currentUser,
            username: popoverConfiguration.currentUser.username || "anonymous",
            name: "temp",
          },
          // eslint-disable-next-line lingui/no-unlocalized-strings -- graphql __typename
          __typename: "PDFHighlight",
          comments: [],
          page: renderProps.highlightAreas[0].pageIndex,
          createdAt: "",
          /* eslint-disable lingui/no-unlocalized-strings -- XFDF XML markup */
          xfdfString: `<?xml version="1.0" encoding="UTF-8" ?>
<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots><highlight page="${
            renderProps.highlightAreas[0].pageIndex
          }" rect="${xfdfData.rect.join(",")}" color="${"#cccccc"}" flags="print" name="${uuidv4()}" 
      title="${
        popoverConfiguration.currentUser.id
      }" subject="Highlight" date="D:20230130160820Z00'00'" creationdate="D:20230130160820Z00'00'" coords="${xfdfData.coords.join(
        ",",
      )}">
<contents>${renderProps.selectedText}</contents>
</highlight>
</annots>
</xfdf>`,
          /* eslint-enable lingui/no-unlocalized-strings */
        } as IPdfHighlight;
        setFakeHighlight(pdfHighlight);
        renderProps.cancel();
      });
  }, [renderProps]);

  return <></>;
}
